

import React, { useState } from 'react';
import {
  Modal,
  Box,
  Grid,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from 'assets/theme/base/colors';

const NewReferralProgramSettingModal = ({ isOpen, onClose, onSave }) => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    is_active: false,
    currency_code: '',
    reward_amount: '',
    hold_days: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.currency_code) errors.currency_code = 'Currency Code is required';
    if (!formData.reward_amount) errors.reward_amount = 'Reward Amount is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = {
        is_active: !!formData.is_active,
        currency_code: formData.currency_code,
        reward_amount: parseFloat(formData.reward_amount),
        hold_days: formData.hold_days ? parseInt(formData.hold_days, 10) : null,
        created_by: user?.id,
        updated_by: user?.id,
      };

      await onSave(payload);
      onClose();
      setFormData({
        is_active: false,
        currency_code: '',
        reward_amount: '',
        hold_days: '',
      });
    } catch (error) {
      console.error('Error saving Referral Program Setting:', error);
      setToast({
        open: true,
        message:
          error.response?.data?.detail ||
          'Error saving Referral Program Setting',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: '600px' },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 3,
              py: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <MDTypography variant="h6" color="white">
              Add Referral Program Setting
            </MDTypography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 4, overflowY: 'auto' }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MDBox mt={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.is_active}
                          onChange={handleInputChange}
                          name="is_active"
                        />
                      }
                      label="Active"
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Currency Code (e.g. GBP, AUD, USD)"
                    name="currency_code"
                    value={formData.currency_code}
                    onChange={handleInputChange}
                    error={!!formErrors.currency_code}
                    helperText={formErrors.currency_code}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reward Amount"
                    name="reward_amount"
                    type="number"
                    value={formData.reward_amount}
                    onChange={handleInputChange}
                    error={!!formErrors.reward_amount}
                    helperText={formErrors.reward_amount}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hold Days (optional)"
                    name="hold_days"
                    type="number"
                    value={formData.hold_days}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </MDButton>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NewReferralProgramSettingModal;


